/**
 * Event Service
 * Lightweight event collection with batched sending
 * No cookies, no device fingerprinting, no external dependencies
 */

import { env } from '@/utils/env';

interface TrackingEvent {
  eventType: number;
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
  eventValue?: number;
  pagePath?: string;
  referrer?: string;
  metadata?: string;
  durationMs?: number;
}

// EventType enum values matching backend
export const EventType = {
  PageView: 0,
  Click: 1,
  FeatureUsage: 2,
  AnalysisStarted: 3,
  AnalysisCompleted: 4,
  AnalysisAbandoned: 5,
  WizardStep: 6,
  ResultCardOpened: 7,
  ShareCreated: 8,
  ShareViewed: 9,
  Login: 10,
  LoginFailed: 11,
  Logout: 12,
  Registration: 13,
  PasswordReset: 14,
  ProfileUpdate: 15,
  CreditPurchase: 16,
  Error: 17,
  Search: 18,
  Export: 19,
  ApiCall: 20,
} as const;

const BATCH_INTERVAL_MS = 30_000; // Send every 30 seconds
const BATCH_SIZE_LIMIT = 20; // Send when buffer reaches 20 events
const MAX_RETRY_BUFFER = 200; // Prevent unbounded growth on persistent failure
const TRACKING_ENDPOINT = '/api/tracking/events';

class EventService {
  private buffer: TrackingEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isEnabled = true;
  private isFlushing = false;
  private optOutCache: boolean | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.startFlushTimer();
      this.setupBeforeUnload();
    }
  }

  /**
   * Check if tracking is opted out (cached, refreshed on localStorage change)
   */
  private checkOptOut(): boolean {
    if (this.optOutCache !== null) return this.optOutCache;
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        this.optOutCache = user.trackingOptOut === true;
        return this.optOutCache;
      }
    } catch {
      // ignore
    }
    return false;
  }

  /**
   * Refresh opt-out cache (call when user profile changes)
   */
  refreshOptOut(): void {
    this.optOutCache = null;
  }

  /**
   * Track a generic event
   */
  track(event: TrackingEvent): void {
    if (!this.isEnabled || this.checkOptOut()) return;

    // Cap buffer to prevent unbounded growth during flush
    if (this.buffer.length >= MAX_RETRY_BUFFER) return;

    this.buffer.push(event);

    if (this.buffer.length >= BATCH_SIZE_LIMIT && !this.isFlushing) {
      this.flush();
    }
  }

  /**
   * Track a page view
   */
  trackPageView(pagePath: string, referrer?: string, durationMs?: number): void {
    this.track({
      eventType: EventType.PageView,
      eventCategory: 'navigation',
      eventAction: 'page_view',
      pagePath,
      referrer,
      durationMs,
    });
  }

  /**
   * Track a click event (from data-track attributes)
   */
  trackClick(trackLabel: string, pagePath?: string): void {
    this.track({
      eventType: EventType.Click,
      eventCategory: 'engagement',
      eventAction: `click_${trackLabel}`,
      eventLabel: trackLabel,
      pagePath,
    });
  }

  /**
   * Track feature usage
   */
  trackFeature(action: string, label?: string, value?: number): void {
    this.track({
      eventType: EventType.FeatureUsage,
      eventCategory: 'feature',
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    });
  }

  /**
   * Track analysis wizard steps
   */
  trackWizardStep(step: number, action: 'enter' | 'complete' | 'abandon'): void {
    this.track({
      eventType: EventType.WizardStep,
      eventCategory: 'analysis',
      eventAction: `wizard_step_${step}_${action}`,
      eventValue: step,
    });
  }

  /**
   * Track analysis lifecycle
   */
  trackAnalysis(action: 'started' | 'completed' | 'abandoned', metadata?: Record<string, unknown>): void {
    const typeMap = {
      started: EventType.AnalysisStarted,
      completed: EventType.AnalysisCompleted,
      abandoned: EventType.AnalysisAbandoned,
    };

    this.track({
      eventType: typeMap[action],
      eventCategory: 'analysis',
      eventAction: `analysis_${action}`,
      metadata: metadata ? JSON.stringify(metadata) : undefined,
    });
  }

  /**
   * Track result card interactions
   */
  trackResultCard(cardName: string, action: 'opened' | 'closed'): void {
    this.track({
      eventType: EventType.ResultCardOpened,
      eventCategory: 'analysis',
      eventAction: `result_card_${action}`,
      eventLabel: cardName,
    });
  }

  /**
   * Track share events
   */
  trackShare(action: 'created' | 'viewed'): void {
    this.track({
      eventType: action === 'created' ? EventType.ShareCreated : EventType.ShareViewed,
      eventCategory: 'engagement',
      eventAction: `share_${action}`,
    });
  }

  /**
   * Disable tracking (opt-out)
   */
  disable(): void {
    this.isEnabled = false;
    this.buffer = [];
  }

  /**
   * Enable tracking
   */
  enable(): void {
    this.isEnabled = true;
  }

  /**
   * Flush buffered events to the backend
   */
  private flush(): void {
    if (this.buffer.length === 0 || this.isFlushing) return;

    this.isFlushing = true;
    const events = [...this.buffer];
    this.buffer = [];

    const url = `${env.apiUrl}${TRACKING_ENDPOINT}`;
    const body = JSON.stringify({ events });

    // Build headers with auth token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    let hasAuth = false;
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        hasAuth = true;
      }
    } catch {
      // ignore
    }

    // Use sendBeacon for page unload scenarios (works without auth headers)
    // The endpoint is AllowAnonymous, so events are accepted without auth
    if (typeof navigator.sendBeacon === 'function' && !hasAuth) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      this.isFlushing = false;
    } else {
      fetch(url, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      })
        .then(() => {
          this.isFlushing = false;
        })
        .catch(() => {
          this.isFlushing = false;
          // Re-queue failed events (with cap to prevent unbounded growth)
          if (this.buffer.length + events.length <= MAX_RETRY_BUFFER) {
            this.buffer.unshift(...events);
          }
        });
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => this.flush(), BATCH_INTERVAL_MS);
  }

  /**
   * Flush using sendBeacon (reliable during page unload, no auth headers needed)
   * The endpoint is AllowAnonymous so events are accepted without auth.
   */
  private flushBeacon(): void {
    if (this.buffer.length === 0) return;

    const events = [...this.buffer];
    this.buffer = [];

    const url = `${env.apiUrl}${TRACKING_ENDPOINT}`;
    const body = JSON.stringify({ events });
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  }

  private setupBeforeUnload(): void {
    // Use sendBeacon for unload — more reliable than fetch during page teardown
    window.addEventListener('beforeunload', () => this.flushBeacon());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushBeacon();
      }
    });
  }

  /**
   * Clean up timers
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Singleton instance
export const eventService = new EventService();
